import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import moment from 'moment-timezone';
import { toast } from 'react-toastify';
import {
  AlertTriangle,
  CalendarPlus,
  ChevronRight,
  Hourglass,
  Undo2,
  Loader2,
  Mail,
  MessageSquare,
  Skull,
  Phone,
  UserCheck,
  X,
} from 'lucide-react';

import { cn } from '../../../lib/utils';
import { CLIENT_ACTIVITY_TEXT, activityLine } from './clientActivityText';
import {
  ONBOARDING_DEAD_REASONS,
  fetchOnboardingTextDraft,
  useClientOnboardingClient,
  useLogTouch,
  useMarkDead,
  useReassignClient,
  useSendBackToVaGov,
  useSendOnboardingText,
  useSetOnboardingAnswer,
  useSetOnboardingAddress,
  useSpecialists,
} from '../../../services/clientOnboarding';
import NudgeComposer from '../onboardingSupport/potentialClients/NudgeComposer';
import PickerMenu from '../onboardingSupport/PickerMenu';
import { formatPhone } from './clientOnboardingUtils';
import ButtonName from './ButtonName';
import ReadinessChecklist, { ReadinessBanner } from './ReadinessChecklist';
import RcCallModal from '../../ringcentral/RcCallModal';
import SlotPickerOverlay from '../scheduling/SlotPickerOverlay';
import { useUser } from '../../../contexts/UserContext';
import { isOwnedBy, NotYoursNote } from '../onboardingSupport/ownership';

// One client (Nic 2026-09-28): the Case Readiness checklist the Onboarding
// Specialist works (same questions and rules as the old panel), plus how to
// reach them and where they stand.

// Matches server/lib/leadReasons REASON_DETAIL_MAX.
export const DEAD_OTHER_MAX = 40;

const LABEL =
  'text-[11px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500';

// The themed picker (the native <select> popup is sharp, square and ignores
// dark mode). Placeholder text reads muted until something is picked.
const PICKER_TRIGGER =
  'h-9 rounded-lg bg-white px-3 text-sm text-slate-700 outline-none ring-1 ring-slate-200 hover:bg-slate-50 focus:outline-none dark:!bg-white/5 dark:text-slate-200 dark:ring-white/10 dark:hover:!bg-white/10';
export const Picker = ({ value, placeholder, options, onPick, disabled = false, title }) => (
  <PickerMenu
    label={options.find((x) => x.value === value)?.label || placeholder}
    options={options}
    onPick={onPick}
    disabled={disabled}
    title={title}
    selectedValue={value}
    menuClassName="rounded-xl"
    itemClassName="px-3 py-2 text-sm"
    emptyLabel="Nobody to pick"
    triggerClassName={cn(PICKER_TRIGGER, !value && '!text-slate-400 dark:!text-slate-500')}
  />
);

const REENGAGED_WORD = {
  inbound_text: 'texted us',
  inbound_call: 'called us',
  upload: 'uploaded a file',
  sign_in: 'signed in',
  va_gov_done: 'said VA.gov is set up',
};

// History in plain words (Nic 2026-10-10: "va_gov_access No" meant nothing
// to a specialist). Every checklist answer, today's and the old yes / no
// ones a history can still hold, by name; a state by its button's words.
const HISTORY_FIELD = {
  itf_state: 'Intent to File',
  itf_date: 'Intent to File date',
  dd214_state: 'DD-214',
  prior_service: 'DD-214 shows prior service',
  all_dd214s: 'All DD-214s in',
  rated_state: 'Disability list',
  private_records_state: 'Private treatment records',
  service_records_state: 'Service treatment record',
  blue_button_state: 'VA Blue Button',
  decision_letters_state: 'Prior VA decision letters',
  // Verify POA (Nic 2026-10-27).
  poa_needed: 'Has a POA to remove',
  poa_removed: 'POA removed',
  // Claims Manager preferences (2026-11-02).
  cm_pref_gender: 'Would rather work with',
  cm_pref_requested: 'Requested Claims Manager',
  cm_pref_note: 'Sensitive topics',
  // Before round 3:
  va_gov_access: 'VA.gov checked on a call',
  itf_present: 'Intent to File on file',
  dd214_uploaded: 'DD-214 uploaded',
  rated_uploaded: 'Disability list uploaded',
  pmr_exist: 'Has private treatment records',
  pmr_uploaded: 'Private treatment records uploaded',
  own_copy: 'Has their service treatment record',
  own_copy_uploaded: 'Service treatment record uploaded',
  foia_submitted: 'FOIA request submitted',
  str_received: 'Service treatment record received',
  blue_button_uploaded: 'Blue Button uploaded',
  letters_uploaded: 'Decision letters uploaded',
};
const HISTORY_VALUE = {
  yes: 'Yes',
  no: 'No',
  todo: 'To do',
  got: 'Got it',
  client: 'Client is getting it',
  requested: 'Requested',
  na: "Doesn't apply",
  // Claims Manager preferences (2026-11-02).
  woman: 'A woman',
  man: 'A man',
  none: 'No preference',
  legacy: 'Not asked (before the step existed)',
};
const historyValueOf = (v) => {
  if (v == null || v === '') return 'cleared';
  if (HISTORY_VALUE[v]) return HISTORY_VALUE[v];
  // A date answer.
  if (/^\d{4}-\d{2}-\d{2}$/.test(String(v))) return moment(v, 'YYYY-MM-DD').format('MMM D, YYYY');
  // A person (the requested Claims Manager): the id says nothing to a reader.
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(v))) return 'chosen';
  return String(v);
};
const byActor = (e) => (e.actor ? ` · ${e.actor}` : '');
// " for Oct 30, 2:00 PM", or nothing without a time.
const callFor = (t) => (t ? ` for ${new Date(t).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}` : '');

// Repeated clicks on the same answer by the same person on the same day read
// as one line: where it ended up (events arrive newest first).
export const tidyHistory = (events = []) =>
  events.filter((e, i) => {
    if (!['answer_changed', 'work_notes_saved'].includes(e.type) || i === 0) return true;
    const newer = events[i - 1];
    // Notes saved again the same day by the same person: one line.
    if (e.type === 'work_notes_saved') {
      return !(newer.type === e.type && newer.actor === e.actor && moment(newer.at).isSame(e.at, 'day'));
    }
    return !(
      newer.type === 'answer_changed' &&
      newer.meta?.fieldId === e.meta?.fieldId &&
      newer.actor === e.actor &&
      moment(newer.at).isSame(e.at, 'day')
    );
  });

// A history line for any event: what happened, in plain words. Who did it is
// the line's hover (eventBy), never the text (Nic 2026-10-21). One with no
// words yet reads "Updated", never its code name.
export const eventLine = (e, fieldLabels) =>
  (EVENT_TEXT[e.type] || (() => 'Updated'))(e.meta || {}, { ...e, actor: null }, fieldLabels);
// A call's or meeting's person counts as "who" too (the staff member on it).
export const eventBy = (e) => {
  const who = e.actor || e.meta?.staffName || e.meta?.hostName || null;
  return who ? `By ${who}` : undefined;
};

// The VA check's verdicts, in History (Confirm service agreement).
const SA_VERDICT_WORD = {
  match: 'matches',
  rating_only_match: 'the rating matches',
  mismatch: 'something is off',
  unclear: "couldn't pin it down",
};

export const EVENT_TEXT = {
  // What the client did themselves (client activity, 2026-10-23), worded
  // once in clientActivityText. VA.gov and the POA keep their own lines below.
  ...Object.fromEntries(
    Object.keys(CLIENT_ACTIVITY_TEXT)
      .filter((t) => !['client_va_gov_done', 'client_poa_done'].includes(t))
      .map((t) => [t, (m) => activityLine(t, m)]),
  ),
  // They signed; Unassigned is where a client waits for a specialist (Nic
  // 2026-10-21: "what does landed in Unassigned mean?").
  opened: () => 'Signed their agreement',
  // Agreement corrections, in plain words (Nic 2026-10-21: the history
  // showed sa_begin_correction and sa_send_service_agreement).
  sa_begin_correction: (m, e) => `Started correcting their agreement${byActor(e)}`,
  sa_cancel_correction: (m, e) => `Canceled the agreement correction${byActor(e)}`,
  sa_send_service_agreement: (m, e) => `Sent the corrected agreement to sign${byActor(e)}`,
  sa_void_agreement: (m, e) => `Voided the corrected agreement${byActor(e)}`,
  // Confirm service agreement (Nic 2026-10-23): the robot's VA check.
  sa_check_started: (m, e) => `${m?.retry ? 'Tried the VA check again' : 'Started the VA check'}${byActor(e)}`,
  sa_check_done: (m) =>
    `VA check: ${SA_VERDICT_WORD[m.verdict] || 'done'}${m.amount != null ? ` (the VA paid $${Number(m.amount).toFixed(2)})` : ''}`,
  sa_check_no_answer: (m) => `VA check: no answer after ${m.attempts || 3} calls`,
  sa_check_cannot_call: (m) => `VA check couldn't call${m.reason ? `: ${m.reason}` : ''}`,
  sa_check_recompared: (m) => `Corrected agreement checked again: ${SA_VERDICT_WORD[m.verdict] || 'needs a look'}`,
  sa_confirmed: (m, e) =>
    `Confirmed the service agreement${byActor(e)}${m.verdict === 'mismatch' ? ' as signed (the VA check showed a difference)' : ''}${m.smcKSaved ? ` (SMC-K updated to the VA's: ${m.smcKSaved.to})` : ''}${m.note ? `: "${m.note}"` : ''}`,
  // Who they went to, by name (Nic 2026-10-23: everything in History,
  // the Hot Transfer included). Who did it is the line's hover.
  owner_assigned: (m) => {
    const to = m.toName ? ` to ${m.toName}` : '';
    // Redirected (2026-10-29): who it was meant for, and why not them.
    if (m.via === 'hot_seat') {
      const instead = m.redirectedFromName
        ? ` (${m.redirectedFromName} ${m.redirect?.reason === 'no_answer' ? "didn't pick up" : 'was not free'})`
        : '';
      return `Hot Transfer${to}${instead}${m.connected === false ? ' (missed)' : ''}`;
    }
    if (m.via === 'reassign') return `Reassigned${to}`;
    if (m.via === 'rotation') return `Assigned${to}`;
    if (m.via === 'reengaged') return `Went${to || ' to a specialist'}`;
    return 'Taken';
  },
  // How the Hot Transfer went, from RingCentral (Nic 2026-10-23).
  hot_seat_result: (m) =>
    m.connected
      ? `Hot Transfer connected${m.toName ? ` with ${m.toName}` : ''}`
      : `Hot Transfer missed: ${m.toName || 'their specialist'} calls them back`,
  client_va_gov_done: () => 'They said VA.gov is set up',
  va_gov_not_ready: (m, e) =>
    `They didn't have VA.gov after all${m.via === 'sales' ? ' (their Sales Rep had said yes)' : m.via === 'client' ? ' (they had said it was set up)' : m.via === 'intake' ? ' (their intake said yes)' : ''}${byActor(e)}`,
  va_gov_sent_back: (m, e) => `Sent back to set up VA.gov${byActor(e)}`,
  va_gov_confirmed: (m, e) => `Confirmed VA.gov for them${byActor(e)}`,
  // Verify POA (Nic 2026-10-27): the step's own events, in plain words.
  poa_needed: (m, e) => `Has a POA to remove${byActor(e)}`,
  poa_not_needed: (m, e) => `No POA to remove${byActor(e)}`,
  poa_email_sent: (m) => (m.emailed === false ? "Couldn't email the POA removal steps" : 'Emailed the POA removal steps'),
  client_poa_done: () => 'They said their POA is removed',
  poa_sent_back: (m, e) => `Sent the POA step back to them${byActor(e)}`,
  poa_confirmed: (m, e) => `Confirmed their POA is removed${byActor(e)}`,
  first_call_started: (m, e) =>
    m.via === 'hot_seat'
      ? `First call: transferred live from Sales${byActor(e)}`
      : m.reason
        ? `Marked the first meeting happened${byActor(e)}: ${m.reason}`
        : `On the first call${byActor(e)}`,
  first_call_undone: (m, e) => `Undid marking the first meeting${byActor(e)}`,
  first_meeting_kept: () => 'First meeting happened',
  first_meeting_no_show: () => 'First meeting marked missed',
  meeting_booked: (m, e) => `Booked their call${callFor(m.startTime)}${byActor(e)}`,
  // Their meeting changed in the app (2026-10-25; words since 2026-10-30, it
  // read "Updated"): moved, canceled, or marked missed.
  meeting_rescheduled: (m, e) => `Moved their call${callFor(m.startTime)}${byActor(e)}`,
  meeting_canceled: (m, e) => `Canceled their call${callFor(m.startTime)}${byActor(e)}`,
  meeting_missed: (m, e) => `They missed their call${callFor(m.startTime)}${byActor(e)}`,
  reengaged: (m, e) =>
    m.via === 'by_hand' || m.via === 'admin'
      ? `Brought back from Stalled${m.toName ? ` to ${m.toName}` : ''}${byActor(e)}${m.reason ? `: ${m.reason}` : ''}`
      : `Came back: ${REENGAGED_WORD[m.via] || 'reached out'}`,
  backfilled: (m) =>
    m.state === 'pool' ? 'Moved over from the old onboarding' : 'Moved over from the old onboarding, as Stalled',
  auto_nurtured: (m) =>
    m.days ? `Stalled: no movement for ${m.days} days` : `Stalled: no contact by business day ${m.day}`,
  back_to_checklist: (m, e) => `Carried over at launch, moved back to the checklist${byActor(e)}`,
  work_notes_saved: (m, e) => `Onboarding notes updated${byActor(e)}`,
  // Birthday and SSN (Nic 2026-10-25): who looked or changed them, never the value.
  identity_viewed: (m, e) => `Viewed their ${m?.field === 'dob' ? 'birthday' : 'SSN'}${byActor(e)}`,
  identity_updated: (m, e) =>
    `Changed their ${(m?.fields || []).map((f) => (f === 'dob' ? 'birthday' : 'SSN')).join(' and ') || 'details'}${byActor(e)}`,
  // What a client's file is (Nic 2026-10-24).
  file_labeled: (m, e) =>
    m.item === 'none'
      ? `Marked ${m.fileName || 'a file'} as not a checklist item${byActor(e)}`
      : `Filed ${m.fileName || 'a file'} under ${m.title || 'a checklist step'}${byActor(e)}`,
  // A call picked up in the app and handed to its owner (2026-11-02).
  call_handed_off: (m, e) => `Call handed to ${m.toName || 'their specialist'}${byActor(e)}`,
  // Found in RingCentral's call log (Nic 2026-10-12).
  // Who was on the call is the hover (Nic 2026-10-21).
  call_detected: (m) => {
    const len = !m.durationSeconds
      ? ''
      : m.durationSeconds < 60
        ? ` · ${m.durationSeconds} sec`
        : ` · ${Math.round(m.durationSeconds / 60)} min`;
    if (m.direction === 'outbound') {
      if (m.connected) return `We called them${len}`;
      return m.result === 'Voicemail' ? 'We called, went to voicemail' : 'We called, no answer';
    }
    if (m.connected) return `They called us${len}`;
    return m.result === 'Voicemail' ? 'They called and left a voicemail' : 'They called, missed';
  },
  moved_to_cm_on_claim: () => 'Moved on to a Claims Manager from the claim',
  claim_dead: () => 'Their claim was moved to Dead',
  reopened: () => 'Back in the Onboarding Workspace from the claim',
  address_added: (m) => `Address added${m.city ? `: ${m.city}, ${m.state}` : ''}`,
  answer_changed: (m, e, labels) =>
    `${HISTORY_FIELD[m.fieldId] || labels[m.fieldId] || 'A checklist answer'}: ${historyValueOf(m.to)}${byActor(e)}`,
  ready_for_cm: () => 'Ready for a Claims Manager',
  back_to_working: () => 'Back to working',
  nurtured: (m) => `Moved to Stalled: ${m.reason}`,
  marked_dead: (m) => `Moved to Dead: ${m.reason}${m.reasonDetail ? ` (${m.reasonDetail})` : ''}`,
  assigned_to_cm: (m) => `Assigned to ${m.to || 'a Claims Manager'}${m.training ? ' (in training)' : ''}`,
  // For Training (2026-10-30): reserved on CM Assignment for a Claims
  // Manager In Training, and back.
  marked_for_training: () => 'Marked for a Claims Manager in training',
  training_mark_removed: () => 'No longer marked for training',
  text_sent: (m, e) =>
    m.vaGovNudge ? `Nudged about VA.gov${e.actor ? ` by ${e.actor}` : ''}` : 'Texted',
  call_logged: (m) => `Call logged${m.note ? `: ${m.note}` : ''}`,
  email_logged: (m) => `Email logged${m.note ? `: ${m.note}` : ''}`,
  text_logged: (m) => `Text logged${m.note ? `: ${m.note}` : ''}`,
};

const ClientOnboardingSlideOver = ({
  onboardingId,
  canManage,
  onClose,
  // Open with the text composer up (a waiting card's Nudge).
  startComposer = false,
  // Read only (boards:view, Nic 2026-10-05): no actions at all.
  viewOnly = false,
}) => {
  const { user, hasPermission } = useUser();
  const q = useClientOnboardingClient(onboardingId);
  const setAnswer = useSetOnboardingAnswer();
  const setAddress = useSetOnboardingAddress();
  const logTouch = useLogTouch();
  const markDead = useMarkDead();
  const sendText = useSendOnboardingText();
  const sendBack = useSendBackToVaGov();
  const reassign = useReassignClient();
  const [composer, setComposer] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  // Call and book, like the Sales Reps (2026-09-28).
  const [calling, setCalling] = useState(false);
  const [booking, setBooking] = useState(false);
  const [toUserId, setToUserId] = useState('');
  const [deadReason, setDeadReason] = useState('');
  const [deadOther, setDeadOther] = useState('');
  const data = q.data;
  const o = data?.onboarding;
  // Answers show the moment they are clicked; the server's reply (and the
  // refetch) settles the step statuses.
  const [pending, setPending] = useState({});
  useEffect(() => setPending({}), [onboardingId, data?.answers]);
  const answers = useMemo(() => ({ ...(data?.answers || {}), ...pending }), [data?.answers, pending]);
  const readyForCm = o?.state === 'ready_for_cm';
  const withCm = o?.state === 'with_cm';
  // A specialist may hand their own client on too (Nic 2026-10-05).
  const canHandOff =
    !viewOnly && !!o && !withCm && (canManage || isOwnedBy(o?.ownerUserId, user?.userId));
  const specialists = useSpecialists(canHandOff);

  useEffect(() => {
    setDeadReason('');
    setDeadOther('');
    setToUserId('');
    setComposer(false);
    setHistoryOpen(false);
    setCalling(false);
    setBooking(false);
  }, [onboardingId]);

  useEffect(() => {
    if (startComposer && data?.onboarding) setComposer(true);
    // Once per open: only when the client (or the ask) changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startComposer, onboardingId, !!data?.onboarding]);

  useEffect(() => {
    if (!onboardingId) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !composer) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onboardingId, composer, onClose]);

  if (!onboardingId) return null;

  const run = async (fn, okText) => {
    try {
      await fn();
      if (okText) toast.success(okText);
    } catch (e) {
      toast.error(e?.response?.data?.msg || "That didn't go through.");
    }
  };

  // VA.gov (Nic 2026-10-05): waiting = theirs to set up, nothing to work;
  // ready = someone said it is set up, checked on the first call.
  const va = data?.vaGov || {};
  const vaWaiting = va.stage === 'waiting';
  const vaReady = va.stage === 'ready';
  // No Take (Nic 2026-10-05): every client has a specialist.
  const unownedWaiting =
    !viewOnly && !!o && !o.ownerUserId && o.state === 'pool' && vaWaiting;
  const shortDate = (at) => (at ? moment(at).format('MMM D') : '');
  // Ownership rule (Nic 2026-09-29): only their specialist works a client.
  // Managers read everything and reassign; they reassign to themselves to act.
  const isMine = isOwnedBy(o?.ownerUserId, user?.userId);
  // Nurture is one-way: read only until they reach out.
  const inNurture = o?.state === 'nurture';
  // Only their specialist works the list. With their CM, it is history.
  const locked = withCm || inNurture || !isMine;
  const fieldLabels = Object.fromEntries(
    (data?.steps || []).flatMap((st) => st.fields.map((f) => [f.id, f.label])),
  );
  // A No on VA.gov never moves them by itself (Nic 2026-10-05): the
  // checklist then recommends sending them back, until it is done.
  const doSendBack = () =>
    run(
      () => sendBack.mutateAsync({ onboardingId }),
      `${o?.firstName || 'They'} ${o?.firstName ? 'is' : 'are'} back in Setting up VA.gov. Their portal asks them to confirm again.`,
    );
  const answer = (fieldId, value) => {
    setPending((p) => ({ ...p, [fieldId]: value }));
    setAnswer
      .mutateAsync({ onboardingId, fieldId, value })
      .then((res) => {
        if (res?.state === 'ready_for_cm' && o?.state !== 'ready_for_cm') {
          toast.success('Everything is in. They moved to Ready for CM.');
        }
      })
      .catch((e) => {
        // Not saved: put the answer back the way the server has it.
        setPending((p) => {
          const next = { ...p };
          delete next[fieldId];
          return next;
        });
        toast.error(e?.response?.data?.msg || "That answer didn't save.");
      });
  };

  return createPortal(
    <div className="fixed inset-0 z-[1060]">
      <div className="absolute inset-0 bg-slate-900/40 dark:bg-black/60" onClick={onClose} aria-hidden />
      <aside className="absolute inset-y-0 right-0 flex w-full max-w-xl flex-col bg-[#F3F5F9] shadow-2xl dark:!bg-surface-page">
        <header className="flex items-start gap-3 border-b border-slate-200/80 bg-white px-5 py-4 dark:border-white/10 dark:!bg-surface-elevated">
          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 items-center gap-2">
              <div className="truncate text-lg font-bold text-slate-800 dark:text-slate-100">
                {o?.name || (q.isLoading ? 'Loading...' : 'Client')}
              </div>
              {/* No way to their claim from here (Nic 2026-10-12). */}
            </div>
            {o ? (
              <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500 dark:text-slate-400">
                {o.phone ? (
                  <a href={`tel:${o.phone}`} className="inline-flex items-center gap-1 text-inherit no-underline hover:text-slate-800 dark:hover:text-white">
                    <Phone size={12} /> {formatPhone(o.phone)}
                  </a>
                ) : null}
                {o.email ? (
                  <a href={`mailto:${o.email}`} className="inline-flex min-w-0 items-center gap-1 truncate text-inherit no-underline hover:text-slate-800 dark:hover:text-white">
                    <Mail size={12} /> {o.email}
                  </a>
                ) : null}
                <span>{o.ownerName ? `With ${o.ownerName}` : 'Waiting for a specialist'}</span>
              </div>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1.5 text-slate-400 outline-none hover:bg-slate-100 focus:outline-none dark:hover:!bg-white/10"
          >
            <X size={18} />
          </button>
        </header>

        {/* Readiness as a strip under the header (Nic 2026-10-05). */}
        {data?.steps ? <ReadinessBanner steps={data.steps} /> : null}

        {q.isLoading || !data ? (
          <div className="flex flex-1 items-center justify-center">
            {q.isError ? (
              <span className="text-sm text-rose-500">
                {q.error?.response?.data?.msg || "That client couldn't be loaded."}
              </span>
            ) : (
              <Loader2 size={20} className="animate-spin text-slate-400" />
            )}
          </div>
        ) : (
          <div className="os-scroll min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
            {withCm ? (
              <div className="rounded-xl bg-slate-100 px-4 py-3 text-sm font-semibold text-slate-600 dark:!bg-white/5 dark:text-slate-300">
                Their Claims Manager has them now. This is the record of their onboarding.
              </div>
            ) : null}

            {!withCm && !inNurture && vaWaiting ? (
              <div className="rounded-xl bg-sky-50 px-4 py-3 dark:!bg-sky-500/10">
                <div className="flex items-center gap-1.5 text-sm font-bold text-sky-900 dark:text-sky-100">
                  <Hourglass size={15} className="shrink-0" /> Setting up VA.gov: their step
                </div>
                <p className="mb-0 mt-1 text-xs leading-relaxed text-sky-800 dark:text-sky-200">
                  {va.bouncedAt
                    ? `It wasn't set up when checked on ${shortDate(va.bouncedAt)}. `
                    : ''}
                  Setting up VA.gov is their step. Their portal walks them
                  through it; once they tap <ButtonName>I&apos;ve done it</ButtonName> they are
                  ready to work. They stall with no sign of life, like everyone: a nudge from us doesn&apos;t count.
                </p>
                {/* Their own client: Text below already opens this same
                    nudge, so no second button (Nic 2026-10-05). Nobody's
                    client has no Text row, so it gets Nudge here. */}
                {unownedWaiting ? (
                  <div className="mt-2.5 flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setComposer(true)}
                      className="inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-lg bg-sky-600 px-3 text-xs font-semibold text-white outline-none hover:bg-sky-500 focus:outline-none"
                    >
                      <MessageSquare size={13} /> Nudge them
                    </button>
                    <span className="text-[11px] text-sky-700/80 dark:text-sky-300/80">
                      {va.nudgedAt ? `Last nudged ${shortDate(va.nudgedAt)}` : 'Not nudged yet'}
                    </span>
                  </div>
                ) : isMine ? (
                  <div className="mt-2 text-[11px] text-sky-700/80 dark:text-sky-300/80">
                    {va.nudgedAt ? `Last nudged ${shortDate(va.nudgedAt)}. ` : ''}
                    Nudge opens the VA.gov text, already written.
                  </div>
                ) : null}
              </div>
            ) : null}

            {/* Not set up on the call and not sent back yet: the reminder
                sits at the top until it is handled (Nic 2026-10-05). */}
            {!withCm && !inNurture && vaReady && va.checkedNo ? (
              <div className="flex flex-wrap items-center gap-3 rounded-xl bg-amber-50 px-4 py-3 ring-1 ring-amber-200 dark:!bg-amber-500/10 dark:ring-amber-500/30">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 text-sm font-bold text-amber-900 dark:text-amber-100">
                    <AlertTriangle size={15} className="shrink-0" /> VA.gov wasn&apos;t set up on the call
                  </div>
                  <div className="mt-0.5 text-xs text-amber-800 dark:text-amber-200">
                    Recommended: send them back to Setting up VA.gov.
                  </div>
                </div>
                {isMine ? (
                  <button
                    type="button"
                    disabled={sendBack.isLoading}
                    onClick={doSendBack}
                    className="inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-lg bg-amber-600 px-3 text-xs font-semibold text-white outline-none hover:bg-amber-500 focus:outline-none disabled:opacity-50"
                  >
                    {sendBack.isLoading ? <Loader2 size={13} className="animate-spin" /> : <Undo2 size={13} />}
                    Send them back
                  </button>
                ) : null}
              </div>
            ) : null}

            {/* Reach them: text (words already written), call, or book. */}
            {inNurture ? (
              <div className="rounded-xl bg-violet-50 px-4 py-3 text-sm font-semibold text-violet-700 dark:!bg-violet-500/10 dark:text-violet-300">
                Stalled. They come back to a specialist on their own when they text, call, upload or sign in.
              </div>
            ) : null}

            {viewOnly ? (
              <div className="rounded-xl bg-slate-100 px-4 py-2.5 text-xs font-semibold text-slate-600 dark:!bg-white/5 dark:text-slate-300">
                View only. You can see everything here; their specialist does the work.
              </div>
            ) : o.ownerUserId && !isMine && !withCm ? (
              <NotYoursNote ownerName={o.ownerName} noun="list" />
            ) : null}

            {isMine && !withCm && !inNurture ? (
              <div className="grid grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={() => setComposer(true)}
                  className="inline-flex h-10 items-center justify-center gap-1.5 whitespace-nowrap rounded-xl bg-sky-600 text-sm font-semibold text-white outline-none hover:bg-sky-500 focus:outline-none"
                >
                  {/* Setting up VA.gov: it opens the written nudge, so it
                      says so (Nic 2026-10-05). */}
                  <MessageSquare size={15} /> {vaWaiting ? 'Nudge' : 'Text'}
                </button>
                <button
                  type="button"
                  disabled={!o.phone}
                  onClick={() => setCalling(true)}
                  className="inline-flex h-10 items-center justify-center gap-1.5 whitespace-nowrap rounded-xl bg-emerald-600 text-sm font-semibold text-white outline-none hover:bg-emerald-500 focus:outline-none disabled:opacity-50"
                >
                  <Phone size={15} /> Call
                </button>
                <button
                  type="button"
                  onClick={() => setBooking(true)}
                  className="inline-flex h-10 items-center justify-center gap-1.5 whitespace-nowrap rounded-xl bg-white text-sm font-semibold text-slate-700 outline-none ring-1 ring-slate-200 hover:bg-slate-50 focus:outline-none dark:!bg-white/5 dark:text-slate-200 dark:ring-white/10 dark:hover:!bg-white/10"
                >
                  <CalendarPlus size={15} /> Book
                </button>
              </div>
            ) : null}

            <ReadinessChecklist
              steps={data.steps}
              answers={answers}
              answerMeta={data.answerMeta || {}}
              vaGov={data.vaGov}
              signedAt={o.signedAt}
              locked={locked}
              onAnswer={answer}
              address={o.address}
              savingAddress={setAddress.isLoading}
              onSaveAddress={(address) =>
                run(() => setAddress.mutateAsync({ onboardingId, address }), 'Address saved and verified.')
              }
            />

            {/* Done here (Nic 2026-10-09): the Claims Manager is picked on
                the CM Assignment page. They can still be worked. */}
            {readyForCm ? (
              <div className="flex items-center gap-2 rounded-xl bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700 dark:!bg-emerald-500/10 dark:text-emerald-300">
                <span className="min-w-0 flex-1">Everything is in. Their Claims Manager is picked on CM Assignment.</span>
                {hasPermission('cm_assignment:manage') ? (
                  <a
                    href="/operations/cm-assignment"
                    className="shrink-0 whitespace-nowrap text-xs font-semibold text-emerald-700 underline-offset-2 hover:underline dark:text-emerald-300"
                  >
                    Open CM Assignment
                  </a>
                ) : null}
              </div>            ) : null}

            {/* Manage this client (Nic 2026-10-05): its own section, set
                apart from the checklist by a labeled rule, one card with a
                row per action. */}
            {(canHandOff && !inNurture) || (!withCm && isMine) ? (
              <div className="flex items-center gap-3 pt-3">
                <span className={cn(LABEL, 'shrink-0')}>Manage this client</span>
                <span className="h-px flex-1 bg-slate-200 dark:!bg-white/10" />
              </div>
            ) : null}
            {(canHandOff && !inNurture) || (!withCm && isMine) ? (
              <section className="divide-y divide-slate-100 rounded-2xl bg-white ring-1 ring-slate-200/70 dark:divide-white/10 dark:!bg-surface-card dark:ring-white/10">
            {canHandOff && !inNurture ? (
              <div className="p-4">
                <div className={cn(LABEL, 'mb-2 flex items-center gap-1.5')}>
                  <UserCheck size={12} /> Give them to another specialist
                </div>
                <div className="flex flex-wrap gap-2">
                  <Picker
                    value={toUserId}
                    placeholder="Pick a specialist"
                    options={(specialists.data?.specialists || [])
                      .filter((sp) => sp.userId !== o.ownerUserId)
                      .map((sp) => ({ value: sp.userId, label: sp.name }))}
                    onPick={setToUserId}
                  />
                  <button
                    type="button"
                    disabled={!toUserId || reassign.isLoading}
                    onClick={() =>
                      run(async () => {
                        await reassign.mutateAsync({ onboardingId, toUserId });
                        setToUserId('');
                      }, 'Reassigned.')
                    }
                    className="inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-lg bg-slate-900 px-4 text-sm font-semibold text-white outline-none hover:bg-slate-700 focus:outline-none disabled:opacity-50 dark:!bg-slate-100 dark:!text-slate-900"
                  >
                    Reassign
                  </button>
                </div>
              </div>
            ) : null}

            {/* Signed clients have no Nurture or Drop: the only way out is
                Dead (Nic 2026-09-28). Their specialist only (2026-09-29): a
                manager reassigns to themselves first. */}
            {!withCm && isMine ? (
              <div className="p-4">
                <div className={cn(LABEL, 'mb-2 flex items-center gap-1.5 !text-rose-500/80')}>
                  <Skull size={12} /> Not going forward?
                </div>
                <div className="flex flex-wrap gap-2">
                  <Picker
                    value={deadReason}
                    placeholder="Why?"
                    options={ONBOARDING_DEAD_REASONS.map((r) => ({ value: r, label: r }))}
                    onPick={setDeadReason}
                  />
                  <button
                    type="button"
                    disabled={
                      !deadReason ||
                      (deadReason === 'Other' && !deadOther.trim()) ||
                      markDead.isLoading
                    }
                    onClick={() => {
                      if (!window.confirm(`Move ${o.name || 'this client'} to Dead? Their claim moves to Dead.`)) return;
                      run(async () => {
                        await markDead.mutateAsync({
                          onboardingId,
                          reason: deadReason,
                          reasonDetail: deadReason === 'Other' ? deadOther.trim() : undefined,
                        });
                        onClose();
                      }, 'Moved to Dead. Their claim is Dead now.');
                    }}
                    className="inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-lg bg-rose-600 px-4 text-sm font-semibold text-white outline-none hover:bg-rose-500 focus:outline-none disabled:opacity-50"
                  >
                    Move to Dead
                  </button>
                </div>
                {/* Other needs a few words, capped so it stays a label
                    (Nic 2026-10-05, same 40 as Sales). */}
                {deadReason === 'Other' ? (
                  <div className="mt-2">
                    <input
                      value={deadOther}
                      maxLength={DEAD_OTHER_MAX}
                      onChange={(e) => setDeadOther(e.target.value)}
                      placeholder="What's the reason?"
                      className="h-9 w-full rounded-lg bg-white px-3 text-sm text-slate-700 outline-none ring-1 ring-slate-200 focus:outline-none focus:ring-slate-300 dark:!bg-white/5 dark:text-slate-100 dark:ring-white/10"
                    />
                    <div className="mt-0.5 text-right text-[10px] tabular-nums text-slate-400">
                      {deadOther.length}/{DEAD_OTHER_MAX}
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}
              </section>
            ) : null}


            {/* Collapsed by default, like Sales (Nic 2026-10-05). */}
            <section>
              <button
                type="button"
                onClick={() => setHistoryOpen((v) => !v)}
                aria-expanded={historyOpen}
                className="flex w-full items-center gap-1.5 text-left outline-none focus:outline-none"
              >
                <ChevronRight
                  size={13}
                  className={cn(
                    'shrink-0 text-slate-400 transition-transform duration-150',
                    historyOpen && 'rotate-90',
                  )}
                />
                <span className={LABEL}>What happened</span>
                <span className="rounded-full bg-slate-900/[0.06] px-1.5 text-[11px] font-semibold tabular-nums text-slate-500 dark:!bg-white/10 dark:text-slate-400">
                  {tidyHistory(data.events).length}
                </span>
              </button>
              {historyOpen ? (
              <ol className="mb-0 mt-2 list-none space-y-1.5 pl-0">
                {tidyHistory(data.events).map((e) => (
                  <li key={e.id} className="flex gap-3 text-xs">
                    <span className="w-28 shrink-0 tabular-nums text-slate-400">
                      {new Date(e.at).toLocaleString([], {
                        month: 'short',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                      })}
                    </span>
                    <span className="min-w-0 text-slate-600 dark:text-slate-300" title={eventBy(e)}>
                      {eventLine(e, fieldLabels)}
                    </span>
                  </li>
                ))}
              </ol>
              ) : null}
            </section>
          </div>
        )}
      </aside>

      {calling && o?.phone ? (
        <RcCallModal
          show
          targetNumber={o.phone}
          onHide={() => setCalling(false)}
          hideRecord
          backdrop={false}
          enforceFocus={false}
          dockOnNarrow
          // Above the slide-over (z-1060).
          style={{ zIndex: 1080 }}
          // A placed call is a touch: their clock restarts.
          onCallPlaced={() => logTouch.mutate({ onboardingId, kind: 'call' })}
        />
      ) : null}
      {booking ? (
        <SlotPickerOverlay
          mount="onboarding"
          leadId={onboardingId}
          leadName={o?.name}
          onClose={() => setBooking(false)}
          onBooked={() => toast.success('Booked. It is on your Meetings calendar.')}
        />
      ) : null}
      <NudgeComposer
        open={composer}
        draftKey={onboardingId}
        name={o?.name}
        loadDraft={() => fetchOnboardingTextDraft(onboardingId)}
        sendText={({ text, rcAccessToken }) =>
          sendText.mutateAsync({ onboardingId, text, rcAccessToken })
        }
        sending={sendText.isLoading}
        onClose={() => setComposer(false)}
      />
    </div>,
    document.body,
  );
};

export default ClientOnboardingSlideOver;
